<template>
  <ListItem :to="{ name: 'insuranceCase', params: { id: insuranceCase.id } }">
    <div class="relative w-full rounded-md bg-white">
      <div class="grid items-center gap-2 py-2 pl-3 pr-2 text-sm md:flex">
        <div class="basis-1/2">
          <p class="font-bold">{{ upperFirst(insuranceCase.insuranceProvider) }}</p>
          <p>{{ insuranceCase.insuranceData?.referenceNumber ?? 'n/a' }}</p>
        </div>

        <div class="basis-1/4">
          <p>
            <UserIcon class="inline-icon text-obsidian-500" />
            {{ insuranceCase.customerData?.firstName ?? 'n/a' }}
            {{ insuranceCase.customerData?.lastName ?? 'n/a' }}
          </p>
          <p>
            <CalendarIcon class="inline-icon text-obsidian-500" />
            {{ createdDate }}
          </p>
        </div>

        <div class="basis-1/4">
          <div class="mb-1">
            <ArrowRightFromLine class="inline-icon text-obsidian-500" />
            <span>{{ startDate }}</span>
          </div>

          <div>
            <ArrowLeftToLine class="inline-icon text-obsidian-500" />
            <span>{{ endDate }}</span>
          </div>
        </div>
      </div>
    </div>
  </ListItem>
</template>

<script setup lang="ts">
import { useFormattedDate } from '@/hooks/use-formatted-date';
import { useI18n } from 'vue-i18n';
import type { InsuranceCase } from '@carvia/ros-client-types';
import { useDateFormat } from '@/hooks/use-date-format';
import ListItem from '@/components/ListItem.vue';
import { UserIcon, CalendarIcon, ArrowRightFromLine, ArrowLeftToLine } from 'lucide-vue-next';
import { upperFirst } from 'lodash';

const props = defineProps<{
  insuranceCase: InsuranceCase;
}>();
const { t } = useI18n();

const startDate = useFormattedDate(props.insuranceCase.startDate, useDateFormat());
const endDate = useFormattedDate(props.insuranceCase.endDate, useDateFormat());
const createdDate = useFormattedDate(props.insuranceCase.createdDate, useDateFormat());
</script>

<i18n lang="json">
{
  "en": {
    "customer": "Customer",
    "start": "Start:",
    "end": "End:",
    "createdAt": "Created at",
    "insuranceProvider": "Insurance provider",
    "referenceNumber": "Reference Nr."
  },
  "de": {
    "customer": "Kunde",
    "start": "Start:",
    "end": "Ende:",
    "createdAt": "Erstellt am",
    "insuranceProvider": "Versicherer",
    "referenceNumber": "Aktenzeichen"
  },
  "es": {
    "customer": "Cliente",
    "start": "Inicio:",
    "end": "Fin:",
    "createdAt": "Creado el",
    "insuranceProvider": "Aseguradora",
    "referenceNumber": "N.º ref."
  }
}
</i18n>
